// Admin-bucket dock signal counts — pending member approvals + org unsent rollup.
//
// SC-6 slice of docs/planning/firefly_role_context_presentation.md §5.3.

import { useEffect, useMemo, useState } from "react";
import type { AuthContextValue } from "@/contexts/AuthContext";
import { canAccessPrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  canRunAdminMemberRpcs,
} from "@/lib/admin/adminDockRoles";
import {
  fetchPendingMemberApprovalCounts,
  totalPendingMemberApprovals,
  type PendingMemberApprovalCounts,
} from "@/lib/admin/pendingMemberApprovalCount";
import { requireSupabase } from "@/lib/supabase";

export type AdminDockSignals = {
  pendingMemberApprovalCounts: PendingMemberApprovalCounts | null;
  pendingMemberApprovalTotal: number | null;
  /** Cross-rep unsent rollup; `null` until a dedicated API lands (stub). */
  orgUnsentRollupCount: number | null;
};

export function useAdminDockSignals(
  auth: AuthContextValue | null,
  scopedOrganizationId: string | null,
): AdminDockSignals {
  const [pendingMemberApprovalCounts, setPendingMemberApprovalCounts] =
    useState<PendingMemberApprovalCounts | null>(null);

  const canFetchApprovals = useMemo(() => {
    if (!auth || !scopedOrganizationId) return false;
    if (!canAccessPrimaryMode("admin", auth)) return false;
    return canRunAdminMemberRpcs(
      (...roles) => auth.hasRole(...roles),
      (...roles) => auth.realHasRole(...roles),
    );
  }, [auth, scopedOrganizationId]);

  useEffect(() => {
    if (!canFetchApprovals || !scopedOrganizationId) {
      setPendingMemberApprovalCounts(null);
      return;
    }

    let cancelled = false;
    const supabase = requireSupabase();

    void fetchPendingMemberApprovalCounts(supabase, scopedOrganizationId).then(
      (counts) => {
        if (!cancelled) setPendingMemberApprovalCounts(counts);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [canFetchApprovals, scopedOrganizationId]);

  const pendingMemberApprovalTotal =
    pendingMemberApprovalCounts === null
      ? null
      : totalPendingMemberApprovals(pendingMemberApprovalCounts);

  return {
    pendingMemberApprovalCounts,
    pendingMemberApprovalTotal,
    orgUnsentRollupCount: null,
  };
}
